import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup, Button, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { filter, find, isEmpty } from 'lodash';

import * as connectorOps from '../../../api/Connectors/connector-operations';
import Connectors, {
  CONNECTOR_TYPES,
  CONNECTOR_SUBTYPES
} from '../../../api/Connectors/Connectors';
import Sources from '../../../api/Sources/Sources';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import PageHeader from '../../atoms/PageHeader';
import Loading from '../../components/Loading';
import TableList from '../../components/TableList';
import NotFound from '../NotFound';

const handleRemove = async (connectorId, history) => {
  try {
    await methodPromise('connectors.softDelete', connectorId);
    Bert.alert('Connection deleted!', 'warning');
    history.push(`/connections`);
  } catch (error) {
    handleErrorInClient(error);
  }
};

const ViewConnector = ({ connector, history, loading, match, sources }) => {
  if (loading) return <Loading />;
  if (connectorOps.isDeleted(connector)) return <NotFound />;

  const relatedSources = filter(sources, ['connectorId', connectorOps.getId(connector)]);
  const tableHeaders = [
    {
      count: relatedSources.length,
      name: 'Sources'
    },
    {
      isHiddenOnXS: true,
      name: 'Last Updated'
    },
    {
      isHiddenOnXS: true,
      name: 'Created'
    },
    {
      isButtonColumn: true
    }
  ];
  const tablePageHeaderProps = {
    hasSearchInput: false,
    isPremium: true,
    newItems: []
  };
  const tableHeaderRowProps = { tableHeaders };
  const tableContentProps = {
    customURL: '/sources',
    deleteButton: false,
    history,
    isPremium: true,
    isTableEmpty: isEmpty(relatedSources),
    itemIdentifier: 'id',
    itemName: 'Sources',
    items: relatedSources,
    loading,
    match,
    numberOfColumns: 4,
    openNewTabButton: true
  };
  const [passwordHidden, setPasswordHidden] = useState(true);

  const { humanValue } = find(CONNECTOR_TYPES.properties, [
    'value',
    connectorOps.getType(connector)
  ]);

  const { humanValue: humanValueSubtype } = find(CONNECTOR_SUBTYPES.properties, [
    'value',
    connectorOps.getSubType(connector)
  ]);
  const redirectToSources = route => {
    history.push(route);
  };
  return (
    <>
      <PageHeader>
        <h4 className="mr-auto">{connectorOps.getName(connector)}</h4>
        <ButtonGroup size="sm">
          <Button onClick={() => history.push(`${match.url}/edit`)} variant="outline-info">
            Edit
          </Button>
          <Button
            onClick={() => handleRemove(connectorOps.getId(connector), history)}
            variant="outline-danger"
          >
            Delete
          </Button>
        </ButtonGroup>
      </PageHeader>
      <Row className="justify-content-center">
        <Col xs={12} md={6}>
          <Form.Group as={Row}>
            <Form.Label column xs={12} sm={6}>
              Type
            </Form.Label>
            <Col xs={12} sm={6}>
              <Form.Control value={humanValue} readOnly plaintext />
            </Col>
          </Form.Group>
          <Form.Group as={Row}>
            <Form.Label column xs={12} sm={6}>
              Subtype
            </Form.Label>
            <Col xs={12} sm={6}>
              <Form.Control value={humanValueSubtype} readOnly plaintext />
            </Col>
          </Form.Group>
          {connectorOps.hasDomain(connector) && (
            <Form.Group as={Row}>
              <Form.Label column xs={12} sm={6}>
                Domain
              </Form.Label>
              <Col xs={12} sm={6}>
                <Form.Control
                  value={connector.domain}
                  readOnly
                  className="text-truncate"
                  plaintext
                />
              </Col>
            </Form.Group>
          )}
          {connectorOps.hasIp(connector) && (
            <>
              <Form.Group as={Row}>
                <Form.Label column xs={12} sm={6}>
                  IP
                </Form.Label>
                <Col xs={12} sm={6}>
                  <Form.Control value={connectorOps.getIp(connector)} readOnly plaintext />
                </Col>
              </Form.Group>
              <Form.Group as={Row}>
                <Form.Label column xs={12} sm={6}>
                  Port
                </Form.Label>
                <Col xs={12} sm={6}>
                  <Form.Control value={connectorOps.getPort(connector)} readOnly plaintext />
                </Col>
              </Form.Group>
            </>
          )}
          {(connectorOps.isPostgreSQL(connector) || connectorOps.isFTP(connector)) && (
            <>
              <Form.Group as={Row}>
                <Form.Label column xs={12} sm={6}>
                  User
                </Form.Label>
                <Col xs={12} sm={6}>
                  <Form.Control value={connectorOps.getUser(connector)} readOnly plaintext />
                </Col>
              </Form.Group>
              <Form.Group as={Row}>
                <Form.Label column xs={12} sm={6}>
                  Password
                </Form.Label>
                <Col xs={12} sm={6}>
                  <InputGroup>
                    <Form.Control
                      value={connectorOps.getPassword(connector)}
                      readOnly
                      type={passwordHidden ? 'password' : 'text'}
                      plaintext
                    />
                    <InputGroup.Append>
                      <Button
                        onClick={() => setPasswordHidden(!passwordHidden)}
                        variant="outline-secondary"
                      >
                        <FontAwesomeIcon icon={passwordHidden ? 'eye-slash' : 'eye'} />
                      </Button>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Form.Group>
            </>
          )}
          {connectorOps.isPostgreSQL(connector) && (
            <>
              <Form.Group as={Row}>
                <Form.Label column xs={12} sm={6}>
                  Database Name
                </Form.Label>
                <Col xs={12} sm={6}>
                  <Form.Control
                    value={connectorOps.getDatabaseName(connector)}
                    readOnly
                    plaintext
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row}>
                <Form.Label column xs={12} sm={6}>
                  Schema
                </Form.Label>
                <Col xs={12} sm={6}>
                  <Form.Control value={connectorOps.getSchema(connector)} readOnly plaintext />
                </Col>
              </Form.Group>
            </>
          )}
          {(connectorOps.isHTTP(connector) || connectorOps.isFTP(connector)) && (
            <>
              <Form.Group as={Row}>
                <Form.Label column xs={12} sm={6}>
                  File Path
                </Form.Label>
                <Col xs={12} sm={6}>
                  <Form.Control value={connectorOps.getFilePath(connector)} readOnly plaintext />
                </Col>
              </Form.Group>
              <Form.Group as={Row}>
                <Form.Label column xs={12} sm={6}>
                  File Type
                </Form.Label>
                <Col xs={12} sm={6}>
                  <Form.Control value={connectorOps.getFileType(connector)} readOnly plaintext />
                </Col>
              </Form.Group>
            </>
          )}
          {connectorOps.isOutput(connector) && (
            <Form.Group as={Row}>
              <Col xs={12} sm={6}>
                <Form.Control value="Output Connection" readOnly plaintext />
              </Col>
            </Form.Group>
          )}
        </Col>
        <Col xs={12} md={6}>
          <TableList
            childProps={{
              tablePageHeaderProps,
              tableHeaderRowProps,
              tableContentProps
            }}
            isFreeUserAtLimit={false}
          />
          <div className="text-right">
            <Button
              variant="info"
              onClick={() => {
                redirectToSources('/connections/new');
              }}
            >
              Connection <FontAwesomeIcon icon={['fa', 'plus']} size="sm" />
            </Button>{' '}
            <Button
              variant="info"
              onClick={() => {
                redirectToSources(`/sources/new`);
              }}
            >
              Sources <FontAwesomeIcon icon={['fa', 'plus']} size="sm" />
            </Button>
          </div>
        </Col>
      </Row>
    </>
  );
};

ViewConnector.defaultProps = {
  connector: {},
  sources: []
};

ViewConnector.propTypes = {
  connector: PropTypes.object,
  history: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  sources: PropTypes.array
};

export default withTracker(({ match }) => {
  const connectorId = match.params.connector_id;
  const connectorsSub = Meteor.subscribe('connectors.view', connectorId);
  const sourcesSub = Meteor.subscribe('sources.connector', connectorId);
  return {
    connector: Connectors.findOne(connectorId),
    loading: !connectorsSub.ready() || !sourcesSub.ready(),
    sources: Sources.find({ deleted: { $eq: 'no' } }).fetch()
  };
})(ViewConnector);
